import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { message, Button, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PickupDataProvider, usePickupData } from '../contexts/PickupDataContext';
import PickupTaskPlanningFilter from './PickupTaskPlanningFilter';
import PickupTaskPlanningTable from './PickupTaskPlanningTable';
import CreateRouteDrawer from './CreateRouteDrawer';
import EditRouteDrawer from './EditRouteDrawer';
import ViewRouteDrawer from './ViewRouteDrawer';
import { useCreatePickupRoute } from '../hooks/useCreatePickupRoute';
import { CreatePickupRouteInput, PickupRouteWithVehicleInfoType } from '../graphql/types/PickupRouteTypes';
import { usePickupRoutesWithDetails } from '../hooks/usePickupRoutesWithDetails';
import { useUpdatePickupRoute } from '../hooks/useUpdatePickupRoute';
import { UpdatePickupRouteInput } from '../graphql/types/PickupRouteTypes';
import { useDeletePickupRoute } from '../hooks/useDeletePickupRoute';
import dayjs from 'dayjs';
import { useCreatePickupRouteHistory } from '../hooks/useCreatePickupRouteHistory';
import { useLatestPickupRouteHistory } from '../hooks/useLatestPickupRouteHistory';
import { PickupRouteHistoryType } from '../graphql/types/PickupRouteHistoryTypes';
import { useTranslation } from 'react-i18next';
import { useCurrentWarehouse } from '../../../../hooks/useCurrentWarehouse';
import { hasResource } from '../../../../utils/resourceAuth';
import { ResourceCode } from '../../../../constants/resourceCodes';

const PickupTaskPlanning: React.FC = () => {
    return (
        <PickupDataProvider>
            <PickupTaskPlanningContent />
        </PickupDataProvider>
    );
};

const PickupTaskPlanningContent: React.FC = () => {
    // 添加Route详情查看的状态管理
    const [routeDetailDrawerVisible, setRouteDetailDrawerVisible] = useState(false);
    const [selectedRouteData, setSelectedRouteData] = useState<PickupRouteWithVehicleInfoType | null>(null);
    
    // 添加Route编辑的状态管理
    const [routeEditDrawerVisible, setRouteEditDrawerVisible] = useState(false);
    const [editingRouteData, setEditingRouteData] = useState<PickupRouteWithVehicleInfoType | null>(null);

    // 添加Create Route的状态管理
    const [createRouteDrawerVisible, setCreateRouteDrawerVisible] = useState(false);

    // 添加Pickup Date状态管理
    const [pickupDate, setPickupDate] = useState<dayjs.Dayjs | null>(dayjs());

    const [isTaskRunning, setIsTaskRunning] = useState(false);
    
    // 添加按钮状态管理，用于控制 Create Pickup Task 按钮是否可点击
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    
    // 添加任务开始时间戳，用于区分新旧任务
    const [taskStartTime, setTaskStartTime] = useState<number | null>(null);

    // 添加刷新key，用于强制重新渲染表格组件
    const [refreshKey, setRefreshKey] = useState(0);

    // 使用 React Router 导航
    const navigate = useNavigate();

    // 使用PickupDataContext
    const { drivers, vehicles, warehouseId } = usePickupData();
    
    // 获取当前warehouse信息
    const { warehouse } = useCurrentWarehouse();
    
    // 使用CreatePickupRoute hook
    const { createRoute, loading: createRouteLoading } = useCreatePickupRoute();

    // 使用UpdatePickupRoute hook
    const { updateRoute, loading: updateRouteLoading } = useUpdatePickupRoute();

    // 使用DeletePickupRoute hook
    const { deleteRoute } = useDeletePickupRoute();

    // 使用usePickupRoutesWithDetails hook获取路线数据
    const { routes, loading: routesLoading, error: routesError, refetch: refetchRoutes } = usePickupRoutesWithDetails(
        warehouseId, 
        pickupDate?.format('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD')
    );

    // 创建历史的mutation
    const { createHistory, loading: createHistoryLoading } = useCreatePickupRouteHistory();

    // 轮询最新历史
    const { histories, startPolling, stopPolling, refetch: refetchLatest } = useLatestPickupRouteHistory(
        warehouseId ? parseInt(warehouseId) : undefined,
        pickupDate?.format('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD'),
        isTaskRunning ? 10000 : undefined
    );
    const {t} = useTranslation();
    const canOperatePickupTask = hasResource([ResourceCode.menu.PICKUP_TASK_OPERATE]);
    // 初次进入页面时检查历史状态
    useEffect(() => {
        const checkInitialStatus = async () => {
            if (warehouseId && pickupDate) {
                try {
                    // 调用 getLatestPickupRouteHistory 检查初始状态
                    const result = await refetchLatest();
                    const initialHistories = result?.data?.getLatestPickupRouteHistory || [];
                    
                    // 检查是否有正在运行的任务（状态不是 APPLIED 和 FAILED）
                    const hasRunningTasks = initialHistories.length > 0 && initialHistories.some((h: PickupRouteHistoryType) => {
                        const isRunning = h.status !== 'APPLIED' && h.status !== 'FAILED';
                        return isRunning;
                    });
                    
                    if (hasRunningTasks) {
                        // 如果有正在运行的任务，禁用按钮并开启轮询
                        setIsButtonDisabled(true);
                        setIsTaskRunning(true);
                        if (startPolling) {
                            startPolling(10000);
                        }
                    } else {
                        // 如果没有历史记录或没有正在运行的任务，确保按钮可用
                        setIsButtonDisabled(false);
                        setIsTaskRunning(false);
                    }
                } catch (error) {
                    console.error('Error checking initial status:', error);
                    // 如果检查失败，默认启用按钮
                    setIsButtonDisabled(false);
                    setIsTaskRunning(false);
                }
            }
        };

        checkInitialStatus();
    }, [warehouseId, pickupDate, refetchLatest, startPolling]);

    // 监听 histories 变化，更新按钮状态
    // 注意：我们已经在handleCreatePickupTask中实现了定时器检查机制
    // 这个useEffect主要用于处理页面刷新或初始加载时已存在的运行中任务
    useEffect(() => {
        // 只在组件初始化时检查是否有运行中的任务
        // 避免在任务执行过程中干扰按钮状态
        if (!taskStartTime && histories) {
            if (histories.length > 0) {
                // 检查是否有正在运行的任务（非 APPLIED 和 FAILED 状态）
                const hasRunningTasks = histories.some((h: PickupRouteHistoryType) => 
                    h.status !== 'APPLIED' && h.status !== 'FAILED'
                );
                
                if (hasRunningTasks && !isTaskRunning) {
                    setIsButtonDisabled(true);
                    setIsTaskRunning(true);
                    if (startPolling) {
                        startPolling(10000);
                    }
                } else if (!hasRunningTasks && isTaskRunning) {
                    // 所有任务都完成了，启用按钮
                    setIsButtonDisabled(false);
                    setIsTaskRunning(false);
                    if (stopPolling) {
                        stopPolling();
                    }
                }
            } else if (isTaskRunning) {
                // 没有历史记录但任务状态是运行中，说明可能有问题，重置状态
                setIsButtonDisabled(false);
                setIsTaskRunning(false);
                if (stopPolling) {
                    stopPolling();
                }
            }
        }
    }, [histories, stopPolling, startPolling]);

    // 确保组件挂载后按钮状态正确初始化
    useEffect(() => {
        // 如果没有 warehouseId 或 pickupDate，确保按钮可用
        if (!warehouseId || !pickupDate) {
            setIsButtonDisabled(false);
            setIsTaskRunning(false);
        }
    }, []); // 只在组件挂载时执行一次

    const allDone = useMemo(() => {
        if (!histories || histories.length === 0) return true; // 没有历史记录时认为所有任务已完成
        return histories.every((h: PickupRouteHistoryType) => h.status === 'FAILED' || h.status === 'APPLIED');
    }, [histories]);

    // 将最新历史状态同步到表格数据上
    const routesWithStatus = useMemo(() => {
        if (!routes) return [] as typeof routes;
        if (!histories || histories.length === 0) return routes;
        const statusMap = new Map<number, string>();
        histories.forEach((h) => statusMap.set(h.routeId, h.status));
        return routes.map((r) => ({ ...r, status: statusMap.get(r.id) ?? (r as any).status }));
    }, [routes, histories]);

    // 处理Route详情查看
    const handleShowRouteDetail = (routeData: PickupRouteWithVehicleInfoType) => {
        setSelectedRouteData(routeData);
        setRouteDetailDrawerVisible(true);
    };

    // 处理Route编辑
    const handleEditRoute = (routeData: PickupRouteWithVehicleInfoType) => {
        setEditingRouteData(routeData);
        setRouteEditDrawerVisible(true);
    };

    const handleDeleteRouteFromTable = (routeData: PickupRouteWithVehicleInfoType) => {
        // 显示删除确认对话框
        Modal.confirm({
            title: t('pickup.pickupTask.pageDetails.titles.confirmDeleteRouteTitle') || '',
            content: t('pickup.pickupTask.messages.confirmDeleteRoute', {routeName: routeData.routeName}) || '',
            okText: t('components.button.delete') || '',
            okType: 'danger',
            cancelText: t('components.button.cancel') || '',
            onOk: async () => {
                try {
                    // 调用API删除路线
                    await deleteRoute(routeData.id);
                    
                    // 删除成功后刷新数据
                    await refetchRoutes();
                    
                    // 刷新邮编总数
                    await refetchTotalZipcodesCount();
                    
                    // 显示成功提示
                    message.success(t('pickup.pickupTask.messages.routeDeletedSuccessfully') || '');
                } catch (error) {
                    console.error('Failed to delete route:', error);
                    // 错误处理已经在hook中完成
                }
            }
        });
    };

    // 处理Create Route
    const handleCreateRoute = () => {
        setCreateRouteDrawerVisible(true);
    };

    // 处理查看未调度预约单 - 跳转到 Appointment 模块
    const handleViewUndispatchedAppointments = (routeData: PickupRouteWithVehicleInfoType) => {
        // 构建查询参数
        const searchParams = new URLSearchParams();
        
        // 添加 Route 筛选条件
        searchParams.set('routeId', routeData.id.toString());
        
        // 添加 Pickup Date 筛选条件
        searchParams.set('expectedPickupDate', pickupDate?.format('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD'));
        
        // 添加状态筛选条件 - 只显示 CREATED 状态的预约单
        searchParams.set('status', 'CREATED');
        
        // 添加确认状态筛选条件
        searchParams.set('confirm', '1');
        
        // 跳转到 Appointment 模块，并传入筛选参数
        navigate(`/pickup/appointment?${searchParams.toString()}`);
    };

    // 刷新邮编总数
    const refetchTotalZipcodesCount = async () => {
        try {
            // 这里需要调用表格组件的refetch方法
            // 由于表格组件已经通过useTotalZipcodesCount获取数据
            // 我们只需要重新触发查询即可
            // 可以通过key的变化来强制重新渲染
            setRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error('Failed to refetch total zipcodes count:', error);
        }
    };

    // 处理Create Route提交
    const handleCreateRouteSubmit = async (values: any) => {
        if (!warehouseId) {
            message.error(t('pickup.pickupTask.messages.warehouseIdRequired') || '');
            return;
        }
        
        try {
            // 准备API调用参数
            const createRouteInput: CreatePickupRouteInput = {
                routeName: values.route,
                warehouseId: parseInt(warehouseId),
                zipcodesListJson: values.zipcodes,
                driverId: parseInt(values.driverId),
                vehicleId: parseInt(values.vehicleId)
            };
            
            // 调用API创建路线
            await createRoute(createRouteInput);
            
            // 创建成功后刷新数据
            await refetchRoutes();
            
            // 刷新邮编总数
            await refetchTotalZipcodesCount();
            
            // 关闭创建抽屉
            setCreateRouteDrawerVisible(false);
            
            // 显示成功提示
            message.success(t('pickup.pickupTask.messages.routeCreatedSuccessfully') || '');
        } catch (error) {
            console.error('Failed to create route:', error);
            // 错误消息已经在hook中处理
        }
    };

    // 创建Pickup任务
    const handleCreatePickupTask = useCallback(async () => {
        // 防止重复点击
        if (isTaskRunning || isButtonDisabled) {
            return;
        }
        
        // 记录任务开始时间并立即禁用按钮，确保UI立即响应
        const startTime = Date.now();
        flushSync(() => {
            setIsTaskRunning(true);
            setIsButtonDisabled(true);
            setTaskStartTime(startTime);
        });
        
        if (!warehouseId) {
            message.error(t('pickup.pickupTask.messages.warehouseIdRequired') || '');
            // 如果验证失败，立即恢复按钮状态
            flushSync(() => {
                setIsTaskRunning(false);
                setIsButtonDisabled(false);
                setTaskStartTime(null);
            });
            return;
        }
        
        try {
            // 2. 先停止之前的轮询，清空历史数据
            if (stopPolling) {
                stopPolling();
            }
            
            // 3. 调用CreatePickupRouteHistory接口
            const historyResult = await createHistory({
                warehouseId: parseInt(warehouseId),
                pickupDate: pickupDate?.format('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD')
            });
            
                flushSync(() => {
                    setIsTaskRunning(false);
                    setIsButtonDisabled(false);
                    setTaskStartTime(null);
                    refetchRoutes();
                });

        } catch (e) {
            console.error('Error in handleCreatePickupTask:', e);
            flushSync(() => {
                setIsTaskRunning(false);
                setIsButtonDisabled(false);
                setTaskStartTime(null);
            });
            // 如果是Warehouse ID检查失败，也需要重置状态
            if (!warehouseId) {
                message.error(t('pickup.pickupTask.messages.warehouseIdRequired') || '');
            }
        }
    }, [warehouseId, pickupDate, createHistory, startPolling, stopPolling, refetchLatest, refetchRoutes, isTaskRunning, isButtonDisabled]);

    // 移除useEffect，改用定时器手动检查

    return (
        <>
            {/* 筛选条件 */}
            <PickupTaskPlanningFilter
                loadings={[]}
                enterLoading={() => {}}
                onCreateRoute={handleCreateRoute}
                onCreatePickupTask={handleCreatePickupTask}
                pickupDate={pickupDate || dayjs()}
                onPickupDateChange={(date) => setPickupDate(date || dayjs())}
                isTaskRunning={isTaskRunning}
                isButtonDisabled={isButtonDisabled}
                warehouse={warehouse}
                disableCreateTask={!canOperatePickupTask}
                disableCreateRoute={!canOperatePickupTask}
            />

            {/* 表格 */}
            <PickupTaskPlanningTable
                key={refreshKey}
                data={routesWithStatus}
                loading={routesLoading}
                onShowRouteDetail={handleShowRouteDetail}
                onEditRoute={handleEditRoute}
                onDeleteRoute={handleDeleteRouteFromTable}
                onViewUndispatchedAppointments={handleViewUndispatchedAppointments}
                warehouseId={warehouseId ? parseInt(warehouseId) : undefined}
                disableEdit={!canOperatePickupTask}
                disableDelete={!canOperatePickupTask}
            />

            {/* Create Route Drawer */}
            <CreateRouteDrawer
                visible={createRouteDrawerVisible}
                onClose={() => setCreateRouteDrawerVisible(false)}
                loading={createRouteLoading}
                onSubmit={handleCreateRouteSubmit}
                driverOptions={drivers.map(driver => ({
                    label: driver.driverName,
                    value: driver.accountId
                }))}
                vehicleOptions={vehicles.map(vehicle => ({
                    label: vehicle.label,
                    value: vehicle.value.toString()
                }))}
                warehouseId={parseInt(warehouseId!)}
                vehicles={vehicles}
            />

            {/* Route详情Drawer */}
            <ViewRouteDrawer
                visible={routeDetailDrawerVisible}
                onClose={() => setRouteDetailDrawerVisible(false)}
                onEdit={() => {
                    // 从详情模式切换到编辑模式
                    setEditingRouteData(selectedRouteData);
                    setRouteDetailDrawerVisible(false); // 关闭详情Drawer
                    setRouteEditDrawerVisible(true);    // 打开编辑Drawer
                }}
                disableEdit={!canOperatePickupTask}
                disableDelete={!canOperatePickupTask}
                onDelete={async () => {
                    if (!selectedRouteData) {
                        message.error(t('pickup.pickupTask.messages.noRouteDataToDelete') || '');
                        return;
                    }
                    
                    // 显示删除确认对话框
                    Modal.confirm({ 
                        title: t('pickup.pickupTask.messages.confirmDeleteRouteTitle') || '',
                        content: t('pickup.pickupTask.messages.confirmDeleteRoute', {routeName: selectedRouteData.routeName}) || '',
                        okText: t('components.button.delete') || '',
                        okType: 'danger',
                        cancelText: t('components.button.cancel') || '',
                        onOk: async () => {
                            try {
                                await deleteRoute(selectedRouteData.id);
                                // 删除成功后刷新数据
                                await refetchRoutes();
                                // 刷新邮编总数
                                await refetchTotalZipcodesCount();
                                // 关闭详情Drawer
                                setRouteDetailDrawerVisible(false);
                                // 显示成功提示
                                message.success(t('pickup.pickupTask.messages.routeDeletedSuccessfully') || '');
                            } catch (error) {
                                console.error('Failed to delete route:', error);
                                // 错误处理已经在hook中完成
                            }
                        }
                    });
                }}
                routeId={selectedRouteData?.id || 0}
                vehicles={vehicles}
                drivers={drivers}
            />

            {/* Route编辑Drawer */}
            <EditRouteDrawer
                visible={routeEditDrawerVisible}
                onClose={() => setRouteEditDrawerVisible(false)}
                loading={updateRouteLoading}
                onSubmit={async (values) => {
                    if (!editingRouteData) {
                        message.error(t('pickup.pickupTask.messages.noRouteDataToUpdate') || '');
                        return;
                    }
                    
                    try {
                        // 准备更新数据
                        const updateInput: UpdatePickupRouteInput = {
                            id: editingRouteData.id,
                            routeName: values.route,
                            zipcodesListJson: values.zipcodes,
                            driverId: parseInt(values.driverId),
                            vehicleId: parseInt(values.vehicleId)
                        };
                        
                        // 调用API更新路线
                        await updateRoute(updateInput);
                        
                        // 更新成功后刷新数据
                        await refetchRoutes();
                        
                        // 刷新邮编总数
                        await refetchTotalZipcodesCount();
                        
                        // 关闭编辑Drawer
                        setRouteEditDrawerVisible(false);
                        
                        // 显示成功提示
                        message.success(t('pickup.pickupTask.messages.routeUpdatedSuccessfully') || '');
                    
                        // 编辑成功后不显示详情页，直接关闭
                        // 用户可以通过点击路线行来查看更新后的详情
                    } catch (error) {
                        console.error('Failed to update route:', error);
                        // 错误处理已经在hook中完成
                    }
                }}
                routeId={editingRouteData?.id || 0}
                driverOptions={drivers.map(driver => ({
                    label: driver.driverName,
                    value: driver.accountId
                }))}
                vehicleOptions={vehicles.map(vehicle => ({
                    label: `${vehicle.vehicleNumber}-${vehicle.licensePlate}`,
                    value: vehicle.id.toString()
                }))}
                warehouseId={warehouseId ? parseInt(warehouseId) : undefined}
                vehicles={vehicles}
                drivers={drivers}
            />
        </>
    )
}

export default PickupTaskPlanning;